import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Grid,
  Stack,
  TextField,
  Button,
  Typography,
  FormControl,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Radio,
} from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import RadioGroup from '@mui/material/RadioGroup';

const SurveyCollectionsForm = ({ surveyCollection, onSave, onClose, assignableSurveys, errors, isLoading = false }) => {
  const [name, setName] = useState(surveyCollection?.name || '');
  const [description, setDescription] = useState(surveyCollection?.description || '');
  const [tableauUrl, setTableauUrl] = useState(surveyCollection?.tableau_url || '');
  const [publish, setPublish] = useState(surveyCollection?.publish || 'yes');
  const [selectedSurveys, setSelectedSurveys] = useState([]);
  const [allSurveys, setAllSurveys] = useState([]);
  const [kind, setKind] = useState(surveyCollection?.kind || 'spark');
  const [audience, setAudience] = useState(surveyCollection?.audience || 'staff');
  const [schoolYear, setSchoolYear] = useState(surveyCollection?.school_year || '');
  const [administration, setAdministration] = useState(surveyCollection?.administration || '');

  useEffect(() => {
    if (surveyCollection && assignableSurveys.length >= 0) {
      const assignedSurveys = surveyCollection.surveys || [];
      const combinedSurveys = [...assignedSurveys, ...assignableSurveys.filter((s) => !assignedSurveys.some((as) => as.id === s.id))];
      setAllSurveys(combinedSurveys);
      setSelectedSurveys(assignedSurveys.map((s) => s.id));
    } else if (assignableSurveys.length > 0) {
      setAllSurveys(assignableSurveys);
    }
  }, [surveyCollection, assignableSurveys]);

  const is_create_mode = () => {
    return surveyCollection == null;
  };

  const is_edit_mode = () => {
    return !is_create_mode();
  };

  const canChangeKind = () => {
    return is_create_mode() ? true : !surveyCollection.completed_percentage > 0;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const surveyCollectionData = {
      name,
      description,
      kind,
      audience,
      publish,
      tableau_url: tableauUrl,
      school_year: schoolYear,
      administration: administration,
      survey_ids: selectedSurveys,
    };

    if (surveyCollection?.id) {
      surveyCollectionData.id = surveyCollection.id;
    }
    onSave(surveyCollectionData);
  };

  const handleKindChange = (event) => {
    setKind(event.target.value);
  };

  const handleSurveysChange = (event) => {
    const {
      target: { value },
    } = event;

    setSelectedSurveys(typeof value === 'string' ? value.split(',') : value);
  };

  const hasErrors = (prop) => errors && errors[prop]?.length > 0;

  return (
    <Box display="flex" flexDirection="column" height="100vh">
      <Box sx={{ overflowY: 'auto', flexGrow: 1, padding: 4 }}>
        <Typography variant="h5">{`${is_edit_mode() ? 'Edit' : 'New'} ${is_edit_mode() ? (kind === 'pulse' ? 'Pulse' : 'Spark') : ''} Survey`}</Typography>
        <Grid container spacing={2} marginTop={2}>
          <Grid size={12}>
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={hasErrors('name')}
              helperText={errors.name}
              size="small"
              variant="outlined"
              fullWidth
              required
            />
          </Grid>
          <Grid spacing={2} size={12}>
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              error={hasErrors('description')}
              helperText={errors.description}
              size="small"
              variant="outlined"
              fullWidth
              multiline
              minRows={3}
            />
          </Grid>
          {canChangeKind() && (
            <Grid spacing={2} size={12}>
              <FormControl>
                <FormLabel id="kind-radio-buttons-group-label">Type</FormLabel>
                <RadioGroup
                  aria-labelledby="kind-radio-buttons-group-label"
                  defaultValue={kind}
                  name="kind-radio-buttons-group"
                  onChange={handleKindChange}
                >
                  <FormControlLabel value="spark" control={<Radio />} label="Spark" />
                  <FormControlLabel value="pulse" control={<Radio />} label="Pulse" />
                </RadioGroup>
              </FormControl>{' '}
            </Grid>
          )}
          {kind === 'spark' && (
            <Grid spacing={2} size={12}>
              <FormControl>
                <FormLabel id="audience-radio-buttons-group-label">Audience</FormLabel>
                <RadioGroup
                  aria-labelledby="audience-radio-buttons-group-label"
                  value={audience}
                  name="audience-radio-buttons-group"
                  onChange={(e) => setAudience(e.target.value)}
                  row
                >
                  <FormControlLabel value="staff" control={<Radio />} label="Staff" />
                  <FormControlLabel value="student" control={<Radio />} label="Student" />
                  <FormControlLabel value="family" control={<Radio />} label="Family" />
                </RadioGroup>
              </FormControl>
            </Grid>
          )}
          {kind === 'spark' && (
            <Grid spacing={2} size={12}>
              <TextField
                label="Tableau URL"
                value={tableauUrl}
                onChange={(e) => setTableauUrl(e.target.value)}
                error={hasErrors('tableau_url')}
                helperText={errors.tableau_url}
                size="small"
                variant="outlined"
                fullWidth
                minRows={3}
              />
            </Grid>
          )}
          <Grid spacing={2} size={12}>
            <FormControl>
              <FormLabel id="publish-radio-buttons-group-label">Publish</FormLabel>
              <RadioGroup
                aria-labelledby="publish-radio-buttons-group-label"
                defaultValue={publish}
                name="publish-radio-buttons-group"
                onChange={(e) => setPublish(e.target.value)}
              >
                <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="no" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>{' '}
          </Grid>
          <Grid size={12}>
            <TextField
              label="School Year"
              value={schoolYear}
              onChange={(e) => setSchoolYear(e.target.value)}
              error={hasErrors('school_year')}
              helperText={errors.school_year}
              size="small"
              variant="outlined"
              fullWidth
            />
          </Grid>
          <Grid size={12}>
            <TextField
              label="Administration"
              value={administration}
              onChange={(e) => setAdministration(e.target.value)}
              error={hasErrors('administration')}
              helperText={errors.administration}
              size="small"
              variant="outlined"
              fullWidth
            />
          </Grid>
          <Grid size={12}>
            <Typography variant="subtitle1" gutterBottom>
              Assign Questionnaires
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                multiple
                fullWidth
                value={selectedSurveys}
                onChange={handleSurveysChange}
                input={<OutlinedInput label="Questionnaires" />}
                renderValue={(selected) => selected.map((id) => allSurveys.find((s) => s.id === id)?.name).join(', ')}
                MenuProps={{
                  slotProps: {
                    paper: {
                      style: {
                        maxHeight: 48 * 4.5 + 8,
                        width: 250,
                      },
                    },
                  },
                }}
                disabled={isLoading}
              >
                {allSurveys.map((survey) => (
                  <MenuItem key={survey.id} value={survey.id}>
                    <Checkbox checked={selectedSurveys.indexOf(survey.id) > -1} />
                    <ListItemText primary={survey.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ position: 'sticky', bottom: 0, backgroundColor: 'white', padding: 2, paddingLeft: 4, boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)' }}>
        <Stack direction="row" spacing={2}>
          <Button variant="contained" onClick={handleSave} sx={{ minWidth: '150px' }}>
            Save
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </Stack>
      </Box>
    </Box>
  );
};

SurveyCollectionsForm.propTypes = {
  surveyCollection: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  confirmCancelRef: PropTypes.object,
};

export default SurveyCollectionsForm;
